import styled, { css } from "styled-components";

import type { Text as TextDef, Token } from "styles/config/text";
import { SpacingStyles } from "../Spacing";
import type { StyledProps } from "./types";

const HeadingBaseStyles = css`
  font-weight: ${({ theme }) => theme.text.weight.semibold};
  line-height: ${({ theme }) => theme.text.lineHeight.tight};
  letter-spacing: ${({ theme }) => theme.text.letterSpacing.tight};
`;

const BodyBaseStyles = css`
  font-size: ${({ theme }) => theme.text.size.body.regular};
  font-weight: ${({ theme }) => theme.text.weight.regular};
  line-height: ${({ theme }) => theme.text.lineHeight.tight};
`;

export const SizeStyles = css<{ $size?: StyledProps["$size"] }>`
  ${({ $size, theme }) => {
    if (!$size) {
      return;
    }
    if ($size.startsWith("header")) {
      const key = $size.split(".")[1] as keyof TextDef["size"]["header"];
      return css`
        font-size: ${theme.text.size.header[key]};
      `;
    }
    if ($size.startsWith("body")) {
      const key = $size.split(".")[1] as keyof TextDef["size"]["body"];
      return css`
        font-size: ${theme.text.size.body[key]};
      `;
    }
  }};
`;

export const Text = styled.span<StyledProps>`
  ${SpacingStyles};

  ${({ $variant, theme }) => {
    const variantStyles: Record<Token, ReturnType<typeof css>> = {
      headerExtraLarge: css`
        ${HeadingBaseStyles};
        font-size: ${theme.text.size.header.extraLarge};
      `,
      headerLarge: css`
        ${HeadingBaseStyles};
        font-size: ${theme.text.size.header.large};
      `,
      headerMedium: css`
        ${HeadingBaseStyles};
        font-size: ${theme.text.size.header.medium};
      `,
      headerRegular: css`
        ${HeadingBaseStyles};
        font-size: ${theme.text.size.header.regular};
      `,
      headerSmall: css`
        ${HeadingBaseStyles};
        font-size: ${theme.text.size.body.small};
        text-transform: uppercase;

        /* Using one-off settings here given this letter-spacing is not tokenised */
        letter-spacing: 5%;
      `,
      body: css`
        ${BodyBaseStyles};
      `,
      bodySemibold: css`
        ${BodyBaseStyles};
        font-weight: ${theme.text.weight.semibold};
      `,
      bodySmall: css`
        ${BodyBaseStyles};
        font-size: ${theme.text.size.body.small};
      `,
      bodySmallSemibold: css`
        ${BodyBaseStyles};
        font-size: ${theme.text.size.body.small};
        font-weight: ${theme.text.weight.semibold};
      `,
      monospace: css`
        font-family: ${theme.text.font.monospace};
        font-size: ${theme.text.size.body.small};
        font-weight: ${theme.text.weight.regular};
        line-height: ${theme.text.lineHeight.normal};
      `,
    };
    return variantStyles[$variant] || css``;
  }};
  ${SizeStyles};
  ${({ $font, theme }) =>
    $font &&
    css`
      font-family: ${theme.text.font[$font]};
    `};
  ${({ $weight, theme }) =>
    $weight &&
    css`
      font-weight: ${theme.text.weight[$weight]};
    `};
  ${({ $lineHeight, theme }) =>
    $lineHeight &&
    css`
      line-height: ${theme.text.lineHeight[$lineHeight]};
    `};
  ${({ $letterSpacing, theme }) =>
    $letterSpacing &&
    css`
      letter-spacing: ${theme.text.letterSpacing[$letterSpacing]};
    `};
  ${({ $color, theme }) =>
    $color &&
    css`
      color: ${theme.activeColorScheme.colors[$color]};
    `};
  ${({ $decoration }) =>
    $decoration &&
    css`
      text-decoration: ${$decoration};
    `};
  ${({ $fontStyle }) =>
    $fontStyle &&
    css`
      font-style: ${$fontStyle};
    `};
  ${({ $align }) =>
    $align &&
    css`
      text-align: ${$align};
    `};
  ${({ theme, $decorationColor }) =>
    $decorationColor &&
    css`
      text-decoration-color: ${theme.activeColorScheme.colors[
      $decorationColor
      ]};
    `};
  ${({ $whiteSpace }) =>
    $whiteSpace &&
    css`
      white-space: ${$whiteSpace};
    `};
  ${({ $textTransform }) =>
    $textTransform &&
    css`
      text-transform: ${$textTransform};
    `};
`;
